import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { BlockTyp } from '../types'
import { BLOCK_TYP_ETIKETT, blockbibliotek } from '../lib/lektionsplanering'
import { useSidTitel } from '../lib/seo'

const TYP_FILTER: { id: BlockTyp | 'alla'; etikett: string }[] = [
  { id: 'alla', etikett: 'Alla' },
  { id: 'uppstart', etikett: 'Uppstart' },
  { id: 'genomgang', etikett: 'Genomgång' },
  { id: 'aktivitet', etikett: 'Aktivitet' },
  { id: 'avslut', etikett: 'Avslut' },
  { id: 'flex_fordjupning', etikett: 'Fördjupning' },
  { id: 'flex_exit_ticket', etikett: 'Exit ticket' },
  { id: 'flex_fardighetstraning', etikett: 'Färdighetsträning' },
  { id: 'flex_case', etikett: 'Case' },
]

/** Blockbiblioteksvyn (STRUKTUR-OCH-WEBBPRESENTATION.md §6.4): bläddra bland
 *  de återanvändbara, versionerade lektionsblocken, filtrerbart på typ, med
 *  utbytesalternativ och vilka lektioner som använder varje block. */
export default function BlockbibliotekSida() {
  useSidTitel('Blockbiblioteket — lektionernas byggblock')
  const [filter, setFilter] = useState<BlockTyp | 'alla'>('alla')
  const synliga = blockbibliotek.filter((b) => filter === 'alla' || b.typ === filter)
  const namnPerId = new Map(blockbibliotek.map((b) => [b.block_id, b.namn]))

  return (
    <div className="wrap">
      <header className="kapitelhuvud">
        <nav className="brodsmula" aria-label="Brödsmulor">
          <Link to="/lararhandledning">← Lärarhandledning</Link>
        </nav>
        <span className="kapitel-nr">Blockbiblioteket</span>
        <h1>Lektionernas byggblock</h1>
        <p className="sektionsingress" style={{ maxWidth: 'var(--max-text)' }}>
          Varje lektion är en komposition av återanvändbara, versionerade block ur det här
          biblioteket. Blocken beskrivs ämnesoberoende och konkretiseras per lektion. Block av
          samma typ kan bytas mot varandra — det är grunden för den kommande lektionsplaneraren.
        </p>
      </header>

      <div className="vyvaxlare vyvaxlare-rullbar" role="group" aria-label="Filtrera på blocktyp">
        {TYP_FILTER.map((t) => (
          <button key={t.id} type="button" className={filter === t.id ? 'aktiv' : ''} onClick={() => setFilter(t.id)}>
            {t.etikett}
          </button>
        ))}
      </div>

      <div className="flexrutnat" style={{ paddingTop: '1.5rem' }}>
        {synliga.map((b) => (
          <div key={`${b.block_id}@${b.version}`} className={`blockkort blockkort-${b.typ}`}>
            <div className="blockkort-huvud">
              <span className={`blocktyp-badge blocktyp-badge-${b.typ}`}>{BLOCK_TYP_ETIKETT[b.typ] ?? b.typ}</span>
              <h4>{b.namn}</h4>
              <span className="blockkort-tid">{b.varaktighet_min} min</span>
            </div>
            <p className="blockkort-instans">{b.generisk_beskrivning}</p>
            {b.kalla_pedagogisk_metod && (
              <p className="blockkort-kalla">
                <strong>Pedagogisk metod: </strong>
                {b.kalla_pedagogisk_metod}
              </p>
            )}
            {b.ersattningsbar_med.length > 0 && (
              <p className="blockkort-alternativ">
                <strong>Kan bytas mot: </strong>
                {b.ersattningsbar_med.map((id) => namnPerId.get(id) ?? id).join(' · ')}
              </p>
            )}
            <p className="blockkort-version">
              Version {b.version}
              {b.anvands_i.length > 0 && (
                <>
                  {' · används i '}
                  {b.anvands_i.map((l, i) => (
                    <span key={l.lektion_id}>
                      {i > 0 && ', '}
                      <Link to={`/lararhandledning/${l.arbetsomrade_id}/lektion/${l.lektion_id}`}>{l.titel}</Link>
                    </span>
                  ))}
                </>
              )}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}
